import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { sendEvent } from '../../Utility/Tracking/Track';
import {
    MissionTrackingEventName,
    TrackingEventName,
    TransactionTrackingEventName,
} from '../../Utility/Tracking/TrackingEvent';
import { AsyncActionStatus } from '../AsyncAction';
import { HelperFriendResponseReducer } from '../Level/HelperFriend/HelperFriendReducer';
import { LevelStatus, LevelType } from '../Level/ILevelState';
import { LevelReducer } from '../Level/LevelReducer';
import { setPlayerState } from '../Player/Action/SetPlayerState';
import { setLives } from '../Player/PlayerInventoryHelper';
import { getCompletedLevels } from '../Player/PlayerLevelHelper';
import { PlayerReducer } from '../Player/PlayerReducer';
import { UIReducer, UIReducerPrio } from '../UI/UIReducer';
import { showPopup } from '../UI/UIStateHelper';
import { UserReducer } from '../User/UserReducer';
import { WorldReducer } from '../World/WorldReducer';
import * as GameEntryPointHelper from './EntryPoint/GameEntryPointHelper';
import { GameActions } from './GameActions';
import GameStateHelper from './GameStateHelper';
import { ContextType } from './IContext';
import { GameScene, IGameState, initialGameState } from './IGameState';
import Game from '../../Game';
import { setNewTurnCardState } from '../Card/CardStateHelper';
import { PlatformReducer } from './PlatformReducer';
import { DataConflictReducer } from './DataConflictReducer';

export const gameReducer: Reducer<IGameState> = (
    state: IGameState = initialGameState,
    action: ActionType<typeof GameActions>
): IGameState => {
    //
    // Always set the previousState reference before handling actions.
    //
    state = {
        ...state,
        previousState: {
            // Copy current state.
            ...state,

            // Don't need all previous states.
            previousState: null,
        },
    };

    //
    //
    //

    if (action.type === getType(GameActions.enableIAP)) {
        if (action.payload) {
            state = GameStateHelper.handleUnconsumedPurchases(
                state,
                action.payload
            );
        }

        return {
            ...state,
            platform: {
                ...state.platform,
                IAPSupport: Game.sdk.hasIAPSupport(),
            },
        };
    }

    if (action.type === getType(GameActions.readyIAP)) {
        return {
            ...state,
            platform: {
                ...state.platform,
                IAPIsReady: true,
            },
        };
    }

    if (
        action.type === getType(GameActions.restoreContext) &&
        state.context.current !== ContextType.DEFAULT
    ) {
        return GameStateHelper.restoreContext(state, {
            status: action.payload.status,
            responseType: action.payload.type,
            data: action.payload.data,
        });
    }

    state = HelperFriendResponseReducer(state, action);

    if (state.gamePaused) {
        return state;
    }

    state = {
        ...state,
    };

    // If level is active clear card tasks.
    if (state.levelState.status === LevelStatus.ACTIVE) {
        state = setNewTurnCardState(state);
    }

    switch (action.type) {
        case getType(GameActions.gameLoaded):
            return {
                ...state,
                gameLoaded: true,
            };

        case getType(GameActions.setLoadedData):
            state = {
                ...state,
                userState: {
                    ...state.userState,
                    ...action.payload.userState,
                },
                platform: {
                    ...state.platform,
                    adsInterstitial: action.payload.platform.adsInterstitial,
                    adsRewarded: action.payload.platform.adsRewarded,
                    subscribeBot: action.payload.platform.subscribeBot,
                    switchGame: action.payload.platform.switchGame,
                    isInstallFromUpdate:
                        action.payload.platform.isInstallFromUpdate,
                    crossPromoSupport:
                        action.payload.platform.crossPromoSupport,
                    // IAPSupport -- May be set async, before or after game loaded. So we do not potentially overwrite it here.
                },
                entryPoint: action.payload.entryPoint,
            };

            // Initial setting of player state.
            state = setPlayerState(state, action.payload.playerState);

            if (action.payload.entryPoint) {
                state = GameEntryPointHelper.handleEntryPoint(
                    state,
                    action.payload.entryPoint
                );
            }

            if (action.payload.restoredPlayerState) {
                state = GameStateHelper.handleRestoredProgressData(
                    state,
                    action.payload.restoredPlayerState
                );
            }

            if (state.playerState.FTUE) {
                sendEvent(TrackingEventName.newPlayer, state);
            }

            sendEvent(TrackingEventName.gameStarted, state);

            return state;

        case getType(GameActions.setContext):
            if (state.gameLoaded && !state.gamePaused) {
                return GameStateHelper.setContext(
                    state,
                    action.payload.context,
                    action.payload.data
                );
            }
            return state;
        case getType(GameActions.setSceneLoaded):
            if (state.activeScene.status === AsyncActionStatus.IN_PROGRESS) {
                return {
                    ...state,
                    activeScene: {
                        ...state.activeScene,
                        status: AsyncActionStatus.COMPLETE,
                    },
                };
            }
            return state;

        case getType(GameActions.setSceneLoading):
            if (state.activeScene.status === AsyncActionStatus.QUEUED) {
                return {
                    ...state,
                    activeScene: {
                        ...state.activeScene,
                        status: AsyncActionStatus.IN_PROGRESS,
                    },
                };
            }
            return state;
        case getType(GameActions.loadWorld):
            if (
                state.activeScene.status === AsyncActionStatus.EMPTY ||
                state.activeScene.status === AsyncActionStatus.COMPLETE
            ) {
                return GameStateHelper.loadWorld(
                    state,
                    action.payload.worldNumber,
                    action.payload.clearUI
                );
            }
            return state;
        case getType(GameActions.loadLevel):
            if (
                state.activeScene.status === AsyncActionStatus.EMPTY ||
                state.activeScene.status === AsyncActionStatus.COMPLETE
            ) {
                // Reset lives to 5 if FTUE and no levels completed.
                // This prevents users from depleting their lives if they quit 5 times during the
                // first level (with no other levels completed).
                if (
                    getCompletedLevels(state.playerState.levels.saga).length ===
                    0
                ) {
                    state = {
                        ...state,
                        playerState: setLives(state.playerState, 5),
                    };
                }

                const spendable =
                    action.payload.levelType === LevelType.SAGA
                        ? state.playerState.lives
                        : 1; // TODO: If it's a challenge level, we should get how many tickets player has.

                if (spendable > 0) {
                    return GameStateHelper.loadLevel(
                        state,
                        action.payload.levelNumber,
                        action.payload.levelPath,
                        action.payload.levelType
                    );
                }

                // Not enough lives - show "No Lives" menu.
                return {
                    ...state,
                    UIState: showPopup(state.UIState, 'noLivesMenu', {}),
                };
            }
            return state;
        case getType(GameActions.restartLevel):
            if (
                state.activeScene.status === AsyncActionStatus.COMPLETE &&
                state.activeScene.result === GameScene.LEVEL
            ) {
                const spendable =
                    state.levelState.levelType === LevelType.SAGA
                        ? state.playerState.lives
                        : 1; // TODO: If it's a challenge level, we should get how many tickets player has.

                if (spendable > 0) {
                    return GameStateHelper.loadLevel(
                        state,
                        state.levelState.levelNumber,
                        state.levelState.levelPath,
                        state.levelState.levelType,
                        state.levelState.attempts + 1
                    );
                } else {
                    // Not enough lives - return to world.
                    state = GameStateHelper.loadWorld(
                        state,
                        state.worldState.worldNumber
                    );

                    // Show the "No Lives Remaining" popup when returning to world map.
                    return {
                        ...state,
                        UIState: showPopup(state.UIState, 'noLivesMenu', {}),
                    };
                }
            }
            return state;
        case getType(GameActions.endLevel):
            state = GameStateHelper.checkDisplayInterstitial(state);
            // Sends the failed mission event if the player ends the level without completing it
            // fired here so it doesnt trigger twice because endLevel is always called, even on a restart
            if (
                state.levelState.status === LevelStatus.FAILED ||
                state.levelState.status === LevelStatus.EXITING
            ) {
                sendEvent(MissionTrackingEventName.missionFailed, state);
                sendEvent(TransactionTrackingEventName.resource, state);
            }

            return {
                ...state,
                levelState: {
                    ...state.levelState,
                    status: action.payload,
                },
            };
    }

    state = PlatformReducer(state, action);

    state = DataConflictReducer(state, action);

    state = {
        ...state,
        ...PlayerReducer(state, action),
    };

    state = {
        ...state,
        ...UserReducer(state, action),
    };

    state = {
        ...state,
        UIState: UIReducer(state.UIState, action),
    };
    state = {
        ...state,
        UIStatePrio: UIReducerPrio(state.UIStatePrio, action),
    };
    if (state.activeScene.status === AsyncActionStatus.COMPLETE) {
        if (state.activeScene.result === GameScene.LEVEL) {
            state = LevelReducer(state, action);
        } else if (state.activeScene.result === GameScene.WORLD) {
            state = WorldReducer(state, action);
        }
    }

    return state;
};
